import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Check, ChefHat, Bike, Home, ChevronLeft, ChevronRight, Phone, Star, Store } from "lucide-react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = "jayek_tracking_map";

// Teardrop pin wrapper (Jahez-style)
function TeardropPin({ color, children }) {
  return (
    <div style={{ position: "relative", width: 44, height: 52, filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.25))" }}>
      <div style={{ position: "absolute", inset: 0, background: color, borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", border: "3px solid #FFFFFF" }} />
      <div style={{ position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)", width: 28, height: 28, background: "#FFFFFF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
      </div>
    </div>
  );
}

// Auto-fit map to show all available points
function FitAllBounds({ points }) {
  const map = useMap();
  const pointsKey = JSON.stringify(points);
  useEffect(() => {
    if (!map || !points.length || !window.google) return;
    if (points.length === 1) {
      map.panTo({ lat: points[0].lat, lng: points[0].lng });
      map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pointsKey]);
  return null;
}

// Dashed route polyline (restaurant -> driver -> home)
function RoutePolyline({ path }) {
  const map = useMap();
  const pathKey = JSON.stringify(path);
  useEffect(() => {
    if (!map || !path || path.length < 2 || !window.google) return;
    const lineSymbol = { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: "#E05D3A", scale: 3 };
    const poly = new window.google.maps.Polyline({
      path,
      strokeOpacity: 0,
      icons: [{ icon: lineSymbol, offset: "0", repeat: "16px" }],
    });
    poly.setMap(map);
    return () => poly.setMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pathKey]);
  return null;
}

const STEPS = [
  { key: "placed", icon: Check, labelKey: "orderPlaced" },
  { key: "preparing", icon: ChefHat, labelKey: "preparing" },
  { key: "on_the_way", icon: Bike, labelKey: "onTheWay" },
  { key: "delivered", icon: Home, labelKey: "delivered" },
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const { t, lang, isRTL } = useLang();
  const [order, setOrder] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  useEffect(() => {
    let prevStatus = null;
    let prevDriverId = null;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        // Status-change notifications
        if (prevStatus && prevStatus !== data.status) {
          const msg =
            data.status === "preparing" ? (lang === "ar" ? "✅ المطعم قبِل طلبك وبدأ التحضير" : "✅ Restaurant accepted your order") :
            data.status === "on_the_way" ? (lang === "ar" ? `🛵 مندوبك ${data.driver_name || ""} في الطريق إليك` : `🛵 Your driver is on the way`) :
            data.status === "delivered" ? (lang === "ar" ? "🎉 تم توصيل طلبك! بالعافية" : "🎉 Your order has been delivered!") :
            data.status === "cancelled" ? (lang === "ar" ? "❌ تم إلغاء الطلب" : "❌ Order was cancelled") :
            null;
          if (msg) toast.success(msg, { duration: 5000 });
        }
        // Driver-assigned notification
        if (!prevDriverId && data.driver_id && data.status !== "placed") {
          toast.success(
            lang === "ar"
              ? `🛵 ${data.driver_name || "المندوب"} استلم الطلب`
              : `🛵 ${data.driver_name || "Driver"} picked up your order`,
            { duration: 5000 }
          );
        }
        prevStatus = data.status;
        prevDriverId = data.driver_id;
        setOrder(data);
        const { data: d } = await api.get(`/orders/${orderId}/driver-location`);
        setDriverInfo(d);
        if (d.driver_rating) setRating(d.driver_rating);
      } catch { /* ignore polling errors */ }
    };
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [orderId, lang]);

  const submitRating = async (stars) => {
    try {
      await api.post(`/orders/${orderId}/rate-driver`, { rating: stars });
      setRating(stars);
      toast.success(lang === "ar" ? "شكرًا على التقييم!" : "Thanks for your rating!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#E05D3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentIdx = STEPS.findIndex((s) => s.key === order.status);
  const activeIdx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/profile" className="text-sm text-[#6B6B6B] inline-flex items-center gap-1 hover:text-[#E05D3A]" data-testid="back-profile">
        <Chevron className="w-4 h-4 rotate-180" /> {t("myOrders")}
      </Link>

      <div className="mt-4 jh-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#E05D3A] font-semibold">#{order.order_id.slice(-6).toUpperCase()}</p>
            <h1 className="font-display text-2xl font-black text-[#1A1A1A] mt-1">
              {lang === "ar" ? order.restaurant_name_ar : order.restaurant_name_en}
            </h1>
          </div>
          <div className="text-end">
            <p className="font-bold text-lg text-[#E05D3A]">{order.total.toFixed(2)} SAR</p>
            <p className="text-xs text-[#6B6B6B]">{order.payment_method === "cash" ? t("cash") : t("card")}</p>
          </div>
        </div>
      </div>

      {/* ===== LIVE TRACKING MAP ===== */}
      <TrackingMap driverInfo={driverInfo} status={order.status} lang={lang} />

      {/* Tracker */}
      <div className="mt-8 jh-card p-8">
        <ol className="space-y-8">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === activeIdx;
            const isDone = idx < activeIdx;
            return (
              <li key={step.key} className="flex items-center gap-5">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${isDone ? "bg-[#00A85A] border-[#00A85A] text-white" : isActive ? "bg-[#E05D3A] border-[#E05D3A] text-white pulse-dot" : "bg-white border-[#EAEAEA] text-[#6B6B6B]"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className={`font-semibold ${isActive ? "text-[#E05D3A]" : isDone ? "text-[#00A85A]" : "text-[#6B6B6B]"}`}>
                    {t(step.labelKey)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-8 jh-card p-6">
        <h3 className="font-bold mb-3">{t("orderSummary")}</h3>        <ul className="text-sm space-y-2 text-[#1A1A1A]">
          {order.items.map((i, k) => (
            <li key={i.menu_item_id || `${i.name_en || i.name_ar}-${k}`} className="flex justify-between gap-3">
              <span>{i.quantity}× {lang === "ar" ? i.name_ar : i.name_en}</span>
              <span>{(i.price * i.quantity).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <div className="h-px bg-[#EAEAEA] my-4" />
        <div className="flex justify-between text-sm"><span>{t("subtotal")}</span><span>{order.subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between text-sm"><span>{t("deliveryFee")}</span><span>{order.delivery_fee.toFixed(2)}</span></div>
        <div className="flex justify-between font-bold text-lg pt-3 border-t border-[#EAEAEA] mt-3"><span>{t("total")}</span><span>{order.total.toFixed(2)} SAR</span></div>
      </div>

      {/* Driver info + map + rating */}
      {driverInfo?.has_driver && (
        <div className="mt-6 jh-card p-5" data-testid="driver-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-[#E05D3A] text-white flex items-center justify-center">
                <Bike className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-[#6B6B6B]">{lang === "ar" ? "مندوبك" : "Your driver"}</p>
                <p className="font-bold text-[#1A1A1A] truncate">{driverInfo.driver_name}</p>
              </div>
            </div>
            {driverInfo.driver_phone && (
              <a href={`tel:${driverInfo.driver_phone}`} className="rounded-full bg-[#00A85A] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="call-driver-btn">
                <Phone className="w-4 h-4" /> {lang === "ar" ? "اتصال" : "Call"}
              </a>
            )}
          </div>

          {order.status === "delivered" && (
            <div className="mt-4 pt-4 border-t border-[#EAEAEA] text-center">
              <p className="text-sm font-semibold mb-2">
                {rating > 0 ? (lang === "ar" ? "شكرًا على تقييمك" : "Thanks for rating") : (lang === "ar" ? "قيّم مندوبك" : "Rate your driver")}
              </p>
              <div className="flex justify-center gap-1" data-testid="driver-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => rating === 0 && submitRating(n)}
                    disabled={rating > 0}
                    className="transition-transform hover:scale-110 disabled:hover:scale-100"
                    data-testid={`rate-${n}`}
                  >
                    <Star className={`w-7 h-7 ${n <= rating ? "fill-[#FFC107] text-[#FFC107]" : "text-[#EAEAEA]"}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ LIVE TRACKING MAP ============
function TrackingMap({ driverInfo, status, lang }) {
  if (!driverInfo || !GOOGLE_MAPS_API_KEY) return null;
  const {
    restaurant_lat, restaurant_lng,
    customer_lat, customer_lng,
    driver_lat, driver_lng,
    has_driver, driver_name,
  } = driverInfo;

  // Collect all available points
  const points = [];
  if (restaurant_lat && restaurant_lng) points.push({ id: "rest", lat: restaurant_lat, lng: restaurant_lng, kind: "store" });
  if (has_driver && driver_lat && driver_lng) points.push({ id: "drv", lat: driver_lat, lng: driver_lng, kind: "driver" });
  if (customer_lat && customer_lng) points.push({ id: "cust", lat: customer_lat, lng: customer_lng, kind: "home" });
  if (!points.length) return null;

  const center = { lat: points[0].lat, lng: points[0].lng };

  // Build polyline in order: restaurant -> driver -> home
  const r = points.find((p) => p.kind === "store");
  const d = points.find((p) => p.kind === "driver");
  const h = points.find((p) => p.kind === "home");
  const lineCoords = [];
  if (status === "on_the_way" && d) {
    if (r) lineCoords.push({ lat: r.lat, lng: r.lng });
    lineCoords.push({ lat: d.lat, lng: d.lng });
    if (h) lineCoords.push({ lat: h.lat, lng: h.lng });
  } else if (r && h) {
    lineCoords.push({ lat: r.lat, lng: r.lng }, { lat: h.lat, lng: h.lng });
  }

  const statusLabel =
    status === "placed" ? (lang === "ar" ? "تم استلام الطلب" : "Order placed") :
    status === "preparing" ? (lang === "ar" ? "المطعم يحضر طلبك" : "Restaurant is preparing") :
    status === "on_the_way" ? (lang === "ar" ? `${driver_name || "المندوب"} في الطريق إليك 🛵` : "Driver on the way 🛵") :
    status === "delivered" ? (lang === "ar" ? "تم توصيل الطلب ✓" : "Delivered ✓") :
    status;

  return (
    <div className="mt-6 jh-card overflow-hidden" data-testid="tracking-map">
      <div className="relative">
        <div style={{ height: 300, width: "100%" }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} language={lang === "ar" ? "ar" : "en"} region="SA">
            <Map
              defaultCenter={center}
              defaultZoom={14}
              mapId={MAP_ID}
              gestureHandling="greedy"
              disableDefaultUI={true}
              zoomControl={false}
              scrollwheel={false}
              clickableIcons={false}
              style={{ width: "100%", height: "100%" }}
            >
              <FitAllBounds points={points} />
              {lineCoords.length >= 2 && <RoutePolyline path={lineCoords} />}
              {r && (
                <AdvancedMarker position={{ lat: r.lat, lng: r.lng }}>
                  <TeardropPin color="#1A1A1A"><Store className="w-4 h-4" color="#1A1A1A" /></TeardropPin>
                </AdvancedMarker>
              )}
              {d && (
                <AdvancedMarker position={{ lat: d.lat, lng: d.lng }}>
                  <TeardropPin color="#00A85A"><Bike className="w-4 h-4" color="#00A85A" /></TeardropPin>
                </AdvancedMarker>
              )}
              {h && (
                <AdvancedMarker position={{ lat: h.lat, lng: h.lng }}>
                  <TeardropPin color="#E05D3A"><Home className="w-4 h-4" color="#E05D3A" /></TeardropPin>
                </AdvancedMarker>
              )}
            </Map>
          </APIProvider>
        </div>

        {/* Status banner */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#1A1A1A] text-white rounded-full px-4 py-2 text-[12px] font-bold z-[500]">
          {statusLabel}
        </div>
      </div>
      {/* Legend */}
      <div className="px-4 py-3 border-t border-[#EAEAEA] flex items-center justify-around text-[11px] font-semibold">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#1A1A1A" }} />
          {lang === "ar" ? "المطعم" : "Restaurant"}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#00A85A" }} />
          {lang === "ar" ? "المندوب" : "Driver"}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#E05D3A" }} />
          {lang === "ar" ? "عنوانك" : "You"}
        </span>
      </div>
    </div>
  );
}
